import React, { useState, useEffect } from 'react'; 
import { RefreshCw } from 'lucide-react';

// Components 
import RulesetSelector from './components/RulesetSelector'; 
import SourceSelector from './components/SourceSelector'; 
import StatModeSelector from './components/StatModeSelector'; 
import CharacterSheet from './components/CharacterSheet';

// Utilities 
import { generateAbilityScores, generateGold } from './utils/diceRoller'; 
import { loadSources, loadAllCharacterData } from './utils/dataFetcher';

function App() { 
  const [selectedRuleset, setSelectedRuleset] = useState(null); 
  const [loading, setLoading] = useState(false); 
  const [error, setError] = useState(null); 
  const [debug, setDebug] = useState({}); 
  const [sources, setSources] = useState([]); 
  const [selectedSources, setSelectedSources] = useState({}); 
  const [statMode, setStatMode] = useState('normal'); 
  const [races, setRaces] = useState([]); 
  const [classes, setClasses] = useState([]); 
  const [backgrounds, setBackgrounds] = useState([]); 
  const [stats, setStats] = useState(null); 
  const [characterClass, setCharacterClass] = useState(null); 
  const [characterRace, setCharacterRace] = useState(null); 
  const [characterBackground, setCharacterBackground] = useState(null); 
  const [equipment, setEquipment] = useState([]); 
  const [gold, setGold] = useState(0);

  const initializeApp = async (ruleset) => { 
    setLoading(true); 
    setError(null); 
    setSelectedRuleset(ruleset);

    try {
      const baseUrl = `/${ruleset}`;
      const sourcesData = await loadSources(baseUrl);
      setSources(sourcesData);

      const initialSelectedSources = {};
      const phbSource = sourcesData.find(s => s.abbreviation === "PHB" || s.name.includes("Player's Handbook"));
      const xphbSource = sourcesData.find(s => s.abbreviation === "XPHB" || s.name.includes("Expanded Player's Handbook"));

      // Apply ruleset-specific source selection rules
      if (ruleset === '2014' && phbSource) {
        // For 2014 ruleset, PHB is required
        initialSelectedSources[phbSource.id] = true;
      } else if (ruleset === '2024') {
        // For 2024 ruleset, both PHB and XPHB are required
        if (phbSource) initialSelectedSources[phbSource.id] = true;
        if (xphbSource) initialSelectedSources[xphbSource.id] = true;
      } else if (phbSource) {
        // Default selection for other rulesets
        initialSelectedSources[phbSource.id] = true;
      } else if (sourcesData.length > 0) {
        // Fallback if PHB isn't found
        initialSelectedSources[sourcesData[0].id] = true;
      }

      setSelectedSources(initialSelectedSources);
      await updateCharacterData(baseUrl, initialSelectedSources);

    } catch (err) {
      console.error("Initialization error:", err);
      setError(`Failed to initialize: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const updateCharacterData = async (baseUrl, selectedSourcesObj) => { 
    try { 
      setLoading(true); 
      const selectedSourceIds = Object.keys(selectedSourcesObj).filter(id => selectedSourcesObj[id]); 
      let data = await loadAllCharacterData(baseUrl, selectedSourcesObj);

      setRaces(data.races);
      setClasses(data.classes);
      data.backgrounds = data.backgrounds.filter(bg => bg.name !== "Custom Background");
      setBackgrounds(data.backgrounds);
      setDebug({
        racesLength: data.races.length,
        classesLength: data.classes.length,
        backgroundsLength: data.backgrounds.length,
        selectedSources: selectedSourceIds
      });
    } catch (err) {
      console.error("Data loading error:", err);
      setError(`Failed to load data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    if (selectedRuleset && Object.keys(selectedSources).some(id => selectedSources[id])) { 
      const baseUrl = `/${selectedRuleset}`; 
      updateCharacterData(baseUrl, selectedSources); 
    } 
  }, [selectedSources, selectedRuleset]);

  const generateRandomCharacter = () => { 
    if (races.length === 0 || classes.length === 0 || backgrounds.length === 0) { 
      setError("Please select sources containing races, classes, and backgrounds."); 
      return; 
    }

    try {
      // Generate ability scores with dice roll info
      const newStats = generateAbilityScores(statMode);
      setStats(newStats);

      // Select random race
      const randomRace = races[Math.floor(Math.random() * races.length)];
      let randomSubrace = null;
      if (randomRace.subraces && randomRace.subraces.length > 0) {
        randomSubrace = randomRace.subraces[Math.floor(Math.random() * randomRace.subraces.length)];
      }
      console.log(races)
      setCharacterRace({ 
        race: randomRace.name, 
        subrace: randomSubrace ? (typeof randomSubrace === 'string' ? randomSubrace : randomSubrace.name) : null 
      });

      // Select random class
      console.log(classes)
      const randomClass = classes[Math.floor(Math.random() * classes.length)];
      let randomSubclass = null;
      if (randomClass.subclasses && randomClass.subclasses.length > 0) {
        randomSubclass = randomClass.subclasses[Math.floor(Math.random() * randomClass.subclasses.length)];
      }
      setCharacterClass({ 
        class: randomClass.name || randomClass.class || "Unknown Class", 
        subclass: randomSubclass ? (typeof randomSubclass === 'string' ? randomSubclass : randomSubclass.name) : null 
      });

      // Select random background
      console.log(backgrounds)
      const randomBackground = backgrounds[Math.floor(Math.random() * backgrounds.length)];
      setCharacterBackground(randomBackground.name || "Commoner");

      // Get equipment from background
      const bgEquipment = randomBackground.equipment || randomBackground.items || [];
      setEquipment(Array.isArray(bgEquipment) ? bgEquipment : [bgEquipment]);

      // Generate gold with dice roll info
      const generatedGold = generateGold(randomClass);
      setGold(generatedGold);
      setError(null);
    } catch (err) {
      console.error("Error generating character:", err);
      setError(`Failed to generate character: ${err.message}`);
    }
  };

  const toggleSource = (sourceId) => { 
    // Enforce ruleset rules
    const phbSource = sources.find(s => s.abbreviation === "PHB" || s.name.includes("Player's Handbook"));
    const xphbSource = sources.find(s => s.abbreviation === "XPHB" || s.name.includes("Expanded Player's Handbook"));
    
    // Don't allow deselecting PHB for 2014 and 2024 rulesets
    if (phbSource && sourceId === phbSource.id && (selectedRuleset === '2014' || selectedRuleset === '2024')) {
      if (selectedSources[sourceId]) {
        return; // Prevent deselection
      }
    }
    
    // Don't allow deselecting XPHB for 2024 ruleset
    if (xphbSource && sourceId === xphbSource.id && selectedRuleset === '2024') {
      if (selectedSources[sourceId]) {
        return; // Prevent deselection
      }
    }
    
    // Normal toggle behavior
    setSelectedSources(prev => ({ ...prev, [sourceId]: !prev[sourceId] })); 
  };

  if (!selectedRuleset) { 
    return <RulesetSelector onSelectRuleset={initializeApp} />; 
  }

  return ( 
    <div className="min-h-screen bg-gray-100 p-4"> 
      <div className="max-w-4xl mx-auto"> 
        <header className="bg-gradient-to-r from-red-700 to-red-900 rounded-t-lg p-6 shadow-lg"> 
          <h1 className="text-3xl font-bold text-white">Random D&D Character Generator</h1> 
          <p className="text-gray-200 mt-2">Using {selectedRuleset} ruleset from 5e.tools</p> 
        </header>

        {debug && (
          <div className="bg-gray-200 p-2 text-xs border border-gray-300 mb-2">
            <p>Races: {debug.racesLength || 0} | Classes: {debug.classesLength || 0} | Backgrounds: {debug.backgroundsLength || 0}</p>
            <p>Selected Sources: {debug.selectedSources?.join(', ') || 'None'}</p>
          </div>
        )}

        <SourceSelector 
          sources={sources} 
          selectedSources={selectedSources} 
          toggleSource={toggleSource}
          ruleset={selectedRuleset} 
        />

        <StatModeSelector statMode={statMode} setStatMode={setStatMode} />

        <div className="mb-4">
          <button
            onClick={generateRandomCharacter}
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-6 rounded-lg flex items-center justify-center"
          >
            {loading ? (
              "Loading..."
            ) : (
              <>
                <RefreshCw className="mr-2" /> Generate Random Character
              </>
            )}
          </button>

          {error && (
            <p className="text-red-600 mt-2">{error}</p>
          )}
        </div>

        <CharacterSheet 
          stats={stats}
          characterClass={characterClass}
          characterRace={characterRace}
          characterBackground={characterBackground}
          equipment={equipment}
          gold={gold}
          statMode={statMode}
        />

        <footer className="text-center text-gray-500 text-sm py-4">
          D&D Character Generator | Developed by Nephele Interactive (nephele.in.th) | Not for commercial propose
        </footer>
      </div>
    </div>
  );
}

export default App;
